import { type InputMaybe } from '@tmdb-graphql-api/schema/types';
import { type PersonOption } from '#components/Filters/PersonMultiSelectDropdown/types.ts';
import { areMarks, areOptions } from '#components/Filters/helpers/otherHelpers.ts';
import { type DiscoverMovieFilters, type DiscoverTvFilters } from '#types.ts';
import { type Mark } from '../../Form/RangeSlider/types.ts';
import { releaseTypeOptions } from '../ReleaseTypeMultiSelectDropdown/index.tsx';
import { tvStatusOptions } from '../TvStatusMultiSelectDropdown/index.tsx';
import { tvTypeOptions } from '../TvTypeMultiSelectDropdown/index.tsx';
import { type MovieFormikValues, type NumberOption, type Option, type TvFormikValues } from '../types.ts';
import { swapLabelsWithValues } from './swapLabelsWithValues.ts';

const transformFirstAired = ({ firstAirDateGte, firstAirDateLte, firstAirDateYear }: DiscoverTvFilters) => {
  if (firstAirDateYear) {
    return [firstAirDateYear, firstAirDateYear];
  }

  if (firstAirDateGte && firstAirDateLte) {
    return [new Date(firstAirDateGte).getFullYear(), new Date(firstAirDateLte).getFullYear()];
  }

  return [];
};

const transformCast = ({ withCast }: DiscoverMovieFilters): PersonOption[] => {
  if (withCast) {
    return withCast.split('|').map(value => ({ id: value, name: '' }));
  }

  return [];
};

const transformCertifications = (
  { certification, certificationGte, certificationLte }: DiscoverMovieFilters,
  marks: Mark[] | Option[] = [],
) => {
  if (!areMarks(marks)) {
    return [];
  }

  if (certification) {
    return swapLabelsWithValues([certification, certification], marks);
  }

  if (certificationGte && certificationLte) {
    return swapLabelsWithValues([certificationGte, certificationLte], marks);
  }

  return [];
};

const transformCompanies = ({ withCompanies }: DiscoverMovieFilters) => {
  if (withCompanies) {
    return withCompanies.split('|').map(value => ({ id: value, name: '' }));
  }

  return [];
};

const transformCrew = ({ withCrew }: DiscoverMovieFilters) => {
  if (withCrew) {
    return withCrew.split('|').map(value => ({ id: value, name: '' }));
  }

  return [];
};

const transformGenres = (genres?: InputMaybe<string>, options: Mark[] | Option[] = []) => {
  if (!areOptions(options)) {
    return [];
  }

  if (genres) {
    return genres.split('|').reduce((acc: Option[], value) => {
      const match = options.find(option => option.id === value);

      if (!match) {
        return acc;
      }

      acc.push(match);
      return acc;
    }, []);
  }

  return [];
};

const transformKeywords = (keywords?: InputMaybe<string>, options: Mark[] | Option[] = []) => {
  if (!areOptions(options)) {
    return [];
  }

  if (keywords) {
    return keywords.split('|').reduce((acc: Option[], value) => {
      const match = options.find(option => option.id === value);

      if (!match) {
        return acc;
      }

      acc.push(match);
      return acc;
    }, []);
  }

  return [];
};

const transformReleaseType = ({ withReleaseType }: DiscoverMovieFilters) => {
  if (!withReleaseType) {
    return [];
  }

  return withReleaseType.split('|').reduce((acc: NumberOption[], value) => {
    const match = releaseTypeOptions.find(option => option.id.toString() === value);

    if (!match) {
      return acc;
    }

    acc.push(match);
    return acc;
  }, []);
};

const transformReleaseYears = ({
  primaryReleaseDateGte,
  primaryReleaseDateLte,
  primaryReleaseYear,
}: DiscoverMovieFilters) => {
  if (primaryReleaseYear) {
    return [primaryReleaseYear, primaryReleaseYear];
  }

  if (primaryReleaseDateGte && primaryReleaseDateLte) {
    return [new Date(primaryReleaseDateGte).getFullYear(), new Date(primaryReleaseDateLte).getFullYear()];
  }

  return [];
};

const transformRuntimes = ({ withRuntimeGte, withRuntimeLte }: DiscoverMovieFilters) => {
  if (withRuntimeGte && withRuntimeLte) {
    return [withRuntimeGte, withRuntimeLte];
  }

  return [];
};

const transformTvStatus = ({ withStatus }: DiscoverTvFilters) => {
  if (!withStatus) {
    return [];
  }

  return withStatus.split('|').reduce((acc: NumberOption[], value) => {
    const match = tvStatusOptions.find(option => option.id.toString() === value);

    if (!match) {
      return acc;
    }

    acc.push(match);
    return acc;
  }, []);
};

const transformTvType = ({ withType }: DiscoverTvFilters) => {
  if (!withType) {
    return [];
  }

  return withType.split('|').reduce((acc: NumberOption[], value) => {
    const match = tvTypeOptions.find(option => option.id.toString() === value);

    if (!match) {
      return acc;
    }

    acc.push(match);
    return acc;
  }, []);
};

const transformVoteAverages = ({ voteAverageGte, voteAverageLte }: DiscoverMovieFilters) => {
  if (voteAverageGte && voteAverageLte) {
    return [voteAverageGte, voteAverageLte];
  }

  return [];
};

export const transformMovieFiltersToValues = (
  filtersParam: DiscoverMovieFilters,
  filterOptions: Record<string, Mark[] | Option[]>,
): MovieFormikValues => ({
  cast: transformCast(filtersParam),
  certifications: transformCertifications(filtersParam, filterOptions.certifications),
  companies: transformCompanies(filtersParam),
  crew: transformCrew(filtersParam),
  releaseType: transformReleaseType(filtersParam),
  releaseYears: transformReleaseYears(filtersParam),
  runtimes: transformRuntimes(filtersParam),
  voteAverages: transformVoteAverages(filtersParam),
  withGenres: transformGenres(filtersParam.withGenres, filterOptions.genres),
  withKeywords: transformKeywords(filtersParam.withKeywords, filterOptions.keywords),
  withoutGenres: transformGenres(filtersParam.withoutGenres, filterOptions.genres),
  withoutKeywords: transformKeywords(filtersParam.withoutKeywords, filterOptions.keywords),
});

export const transformTvFiltersToValues = (
  filtersParam: DiscoverTvFilters,
  filterOptions: Record<string, Mark[] | Option[]>,
): TvFormikValues => ({
  companies: transformCompanies(filtersParam),
  firstAired: transformFirstAired(filtersParam),
  runtimes: transformRuntimes(filtersParam),
  screenedTheatrically: filtersParam.screenedTheatrically ?? false,
  status: transformTvStatus(filtersParam),
  type: transformTvType(filtersParam),
  voteAverages: transformVoteAverages(filtersParam),
  withGenres: transformGenres(filtersParam.withGenres, filterOptions.genres),
  withKeywords: transformKeywords(filtersParam.withKeywords, filterOptions.keywords),
  withoutGenres: transformGenres(filtersParam.withoutGenres, filterOptions.genres),
  withoutKeywords: transformKeywords(filtersParam.withoutKeywords, filterOptions.keywords),
});
